import React, { useEffect } from "react";
import { strings } from "../../resources/strings";
import { styles } from "../../shared-styles/styles";
import { EventType } from "../../defs/Types";
import { ApptInfoTile } from "../../components";
import { GET_FUTURE_APPOINTMENTS } from "../../apis/queries/getFutureAppointments";
import { useQuery } from "@apollo/client";
import { Appointment } from "../../apis/API";
import Utils from "../../utils/UtilityFunctions";

const Appointments = () => {
  const {
    loading: apptsLoading,
    error: apptsError,
    data: apptsData,
  } = useQuery(GET_FUTURE_APPOINTMENTS);
  const [appointments, setAppointments] = React.useState<Appointment[]>([]);

  useEffect(() => {
    processAppointmentsData(apptsData);
  }, [apptsData]);

  const processAppointmentsData = (data: any) => {
    if (data?.getFutureAppointments.length > 0) {
      const futureAppointments: Appointment[] = data.getFutureAppointments.map(
        (appointment: Appointment) => appointment,
      );
      setAppointments(futureAppointments);
    } else {
      // Reset appointments when no data or empty data
      setAppointments([]);
    }
  };

  const renderOrderedAppointments = () => {
    if (apptsLoading) {
      // Show loading cards while data is loading
      return Array.from({ length: 2 }, (_, idx) => (
        <ApptInfoTile
          key={`loading-appt-${idx}`}
          appt={{} as any}
          isLoading={true}
        />
      ));
    }

    const filteredAppointments = appointments.filter(
      (appointment) => appointment.type === EventType.Appointment,
    );

    if (filteredAppointments.length === 0) {
      return null;
    }

    return filteredAppointments.map((appt, idx) => (
      <ApptInfoTile key={idx} appt={appt} />
    ));
  };

  const renderCourtDates = () => {
    if (apptsLoading) {
      // Show loading cards while data is loading
      return Array.from({ length: 2 }, (_, idx) => (
        <ApptInfoTile key={`loading-court-${idx}`} isLoading={true} />
      ));
    }

    const filteredCourtDates = appointments.filter(
      (appointment) => appointment.type === EventType.CourtDate,
    );

    if (filteredCourtDates.length === 0) {
      return null;
    }

    return filteredCourtDates.map((courtAppt, idx) => (
      <ApptInfoTile key={idx} court={courtAppt} />
    ));
  };

  const hasCourtDates =
    appointments.filter(
      (appointment) => appointment.type === EventType.CourtDate,
    ).length > 0;
  const hasAppointments =
    appointments.filter(
      (appointment) => appointment.type === EventType.Appointment,
    ).length > 0;

  return (
    <main id="main-content" data-theme="repath">
      {apptsError && (
        <>
          {console.error(apptsError)}
          {Utils.toastError(strings.toast.loadAppointmentsError)}
        </>
      )}

      {/* The page had no title of its own — only two section headers — so a
          screen-reader user had no h1 to orient from. sr-only keeps the visual
          design unchanged. */}
      <h1 className="sr-only">{strings.pages.appointments}</h1>
      {/* Render court-ordered appointments and court dates tiles*/}
      <div className="flex flex-col justify-between space-y-5">
        <div className={styles.pageHeader}>
          <h2>{strings.appointments.courtHeader}</h2>
          {!apptsLoading && !hasCourtDates && (
            <p>{strings.appointments.noUpcomingCourtDates}</p>
          )}
        </div>
        <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-4 px-5 py-5">
          {renderCourtDates()}
        </div>

        <div className={styles.pageHeader}>
          <h2>{strings.appointments.apptsHeader}</h2>
          {!apptsLoading && !hasAppointments && (
            <p>{strings.appointments.noUpcomingAppts}</p>
          )}
        </div>
        <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-4 px-5 py-5">
          {renderOrderedAppointments()}
        </div>
      </div>
    </main>
  );
};

export default Appointments;
